import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

export const VideoShowcase = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !("IntersectionObserver" in window)) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.35) {
          video.pause();
        }
      },
      { threshold: [0, 0.35, 0.6] }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      id="video"
      data-testid="video-showcase-section"
      className="relative overflow-hidden scroll-mt-24"
      aria-labelledby="video-showcase-title"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/2 left-1/2 h-[420px] w-[760px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00DC82]/[0.045] blur-[150px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 py-24 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center flex flex-col gap-5"
        >
          <h2
            id="video-showcase-title"
            data-testid="video-showcase-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.1]"
          >
            See MockTP in action
          </h2>
          <p
            data-testid="video-showcase-subtitle"
            className="text-base md:text-lg text-slate-400 leading-relaxed"
          >
            See how MockTP turns assessments into clear student progress, professional
            reports and a complete learning record.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="mt-14 rounded-3xl border border-white/[0.08] bg-white/[0.03] p-2 shadow-[0_30px_90px_-35px_rgba(0,0,0,0.8)]"
        >
          <div className="aspect-video overflow-hidden rounded-[1.25rem] bg-[#030712]">
            <video
              ref={videoRef}
              data-testid="mocktp-overview-video"
              className="h-full w-full object-contain"
              controls
              preload="metadata"
              playsInline
              aria-label="MockTP promotional overview video"
            >
              <source src="/video/mocktp-overview.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
